// The offline sync engine's photo step (BUG-2124, ADR 066 — Offline Mode § 2 and § 5.1): after
// every saved auction's lots, each auction's owed thumbnails, resumably, giving way to the next run.
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import {
  isOfflineStoreFullError,
  type OfflineAuctionRecord,
  type OfflineStore,
  type OfflineThumbnailAggregate,
} from '../../gateways/native-device';
import { aggregateOf, measureAuctionBytes } from './apply-auction-delta';
import { syncAuctionThumbnails, type AuctionThumbnailOutcome, type ThumbnailProgress } from './offline-thumbnails';

/** One auction's photo count: thumbnails on the device, of those it should have. */
export interface OfflineAuctionPhotoCount {
  saved: number;
  total: number;
}

/** One run's failures, reported once the run ends. */
export interface RunOutcome {
  failed: boolean;
  storageFull: boolean;
  fail: (message: string, err: unknown) => void;
}

/** What the photo step needs from the engine that owns it. */
export interface PhotoStepHost {
  /** The scope's device store; thumbnails are downloaded, read and written only through `store.thumbnails`. */
  store: OfflineStore;
  sleep?: (ms: number) => Promise<void>;
  /** True once the engine stopped or its scope is no longer the active session (BUG-2064). */
  halted: () => Promise<boolean>;
  /** True once the photo download should give way, for this auction (a run waiting, offline, not in use). */
  shouldStop: (auctionId: string) => boolean;
  /** The auction's Save offline switch, re-read at call time. */
  isSwitchedOn: (row: AuctionListItem) => boolean;
  /** @returns whether the auction was switched off during its pass, clearing the mark. */
  takeCancelled: (auctionId: string) => boolean;
  /** Remove the auction's lots, thumbnails and record from the device. */
  dropAuction: (auctionId: string) => Promise<void>;
  /** Publish the auction's photo count (null once the pass is done) and its size, in one change. */
  publishProgress: (input: { auctionId: string; photos: OfflineAuctionPhotoCount | null; bytes: number; syncedAt: string }) => void;
}

/** The photo step's surface. */
export interface OfflinePhotoStep {
  /**
   * Each saved auction's owed thumbnails, on every run, scoped or not: a switch turned on
   * mid-download must not strand the others. It stops at the next batch when the host says to
   * give way, and the auction record keeps what is still owed, so the next run resumes there.
   *
   * @param input.targets - the switched-on auctions the run listed.
   * @param input.lotsFailed - auctions whose lot read failed this run: their photos wait.
   * @param input.outcome - the run's failure record.
   * @returns once the step has finished or given way.
   * @sideEffect Downloads thumbnails, writes and deletes them, and rewrites auction records.
   */
  run: (input: { targets: AuctionListItem[]; lotsFailed: Set<string>; outcome: RunOutcome }) => Promise<void>;
  /** @returns whether this auction's pass is running now (a switch-off then waits for its batch). */
  isRunning: (auctionId: string) => boolean;
  /** Let the automatic runs try these auctions' photos again (the operator turned a switch on). */
  clearOutOfStorage: (auctionIds: Iterable<string> | 'all') => void;
}

/**
 * @param count - how many thumbnails failed.
 * @param auctionId - their auction.
 * @returns the log line for them.
 */
function thumbnailFailureMessage(count: number, auctionId: string): string {
  return `offline sync: ${count} thumbnail${count === 1 ? '' : 's'} of auction ${auctionId} failed to download`;
}

/**
 * Keep an auction's thumbnail aggregate, and its size, on its record (BL-2609).
 *
 * @param input - the store, the auction, its lots' bytes and its thumbnails' aggregate.
 * @returns once written; nothing when the auction is gone.
 * @sideEffect Rewrites the auction record.
 */
async function saveThumbnailAggregate(input: {
  store: OfflineStore;
  auctionId: string;
  lotBytes: number;
  thumbnails: OfflineThumbnailAggregate;
}): Promise<void> {
  const current = await input.store.records.get({ collection: 'auctions', key: input.auctionId });
  if (current == null) return;
  const value = { ...current, thumbnails: input.thumbnails, bytes: input.lotBytes + input.thumbnails.bytes };
  await input.store.records.put({ collection: 'auctions', key: input.auctionId, value });
}

/**
 * @param host - what the step needs from its engine.
 * @returns the photo step.
 */
export function createOfflinePhotoStep(host: PhotoStepHost): OfflinePhotoStep {
  /**
   * Saved auctions whose photos ran out of device storage (BUG-2124, operator decision Q2): their
   * lots stay, and the automatic triggers skip their photo pass until a switch is turned on.
   */
  const outOfStorage = new Set<string>();
  let running: string | null = null;

  /** One auction's pass, then its record updated with what is still owed and its size. */
  const syncAuctionPhotos = async (auctionId: string): Promise<AuctionThumbnailOutcome | null> => {
    const { store } = host;
    // Running before the first await (as the lot step marks downloading): a switch-off anywhere in
    // the pass, its record reads included, is held and then wins in the finally.
    running = auctionId;
    let pass: AuctionThumbnailOutcome | null = null;
    let cancelled = false;
    let record: OfflineAuctionRecord | null = null;
    let lotBytes = 0;
    let latest: ThumbnailProgress | null = null;
    try {
      record = await store.records.get({ collection: 'auctions', key: auctionId });
      if (record == null) return null;
      const syncedAt = record.syncedAt;
      // A record synced before BL-2429 has never had thumbnails: check every lot once.
      const owed = record.thumbnailRetryLotIds ?? 'all';
      if (owed !== 'all' && owed.length === 0) return null;
      // The lots' share of the size, from the parent index; the thumbnails' comes from the pass.
      lotBytes = aggregateOf(await store.records.entriesByParent({ collection: 'lots', parentKey: auctionId })).bytes;
      let last: OfflineAuctionPhotoCount | null = null;
      pass = await syncAuctionThumbnails({
        store,
        auctionId,
        lotIds: owed,
        shouldStop: () => host.shouldStop(auctionId),
        onProgress: (progress) => {
          latest = progress;
          last = { saved: progress.saved, total: progress.total };
          host.publishProgress({ auctionId, photos: last, bytes: lotBytes + progress.thumbnails.bytes, syncedAt });
        },
        // The auction's aggregate is kept after every batch, so a kill mid-pass leaves the
        // Storage sheet's size near what is on the disk (BL-2609).
        onBatchStored: (progress) => saveThumbnailAggregate({ store, auctionId, lotBytes, thumbnails: progress.thumbnails }),
        sleep: host.sleep,
      });
      if (!(await host.halted())) await saveOwed({ auctionId, pass, last });
    } catch (err) {
      // A pass that failed (a full disk, a store error) is no longer downloading: its count goes,
      // and its size keeps what it stored. The record's owed list stands, so a later pass resumes.
      if (record != null) {
        const bytes = latest == null ? record.bytes : lotBytes + (latest as ThumbnailProgress).thumbnails.bytes;
        host.publishProgress({ auctionId, photos: null, bytes, syncedAt: record.syncedAt });
      }
      throw err;
    } finally {
      // Running until the record is written: a switch-off in that window is held, then wins here,
      // so the write can never bring back an auction it removed.
      running = null;
      cancelled = host.takeCancelled(auctionId);
      if (cancelled) await host.dropAuction(auctionId);
    }
    return cancelled ? null : pass;
  };

  /** Write what the pass left owed, and the auction's size, to its record (nothing when it is gone). */
  const saveOwed = async (input: {
    auctionId: string;
    pass: AuctionThumbnailOutcome;
    last: OfflineAuctionPhotoCount | null;
  }): Promise<void> => {
    const { auctionId, pass, last } = input;
    const { store } = host;
    // Measured, not added up, whether the pass finished or stopped: the store's count is cheap
    // (an index or the in-memory folder listing) and heals a Save's concurrent bump (BL-2609).
    const size = await measureAuctionBytes({ store, auctionId });
    const current = await store.records.get({ collection: 'auctions', key: auctionId });
    if (current == null) return;
    const value = { ...current, bytes: size.bytes, thumbnails: size.thumbnails, thumbnailRetryLotIds: pass.owedLotIds };
    await store.records.put({ collection: 'auctions', key: auctionId, value });
    // A stopped pass keeps its count on show until the next run resumes it.
    host.publishProgress({ auctionId, photos: pass.stopped ? last : null, bytes: size.bytes, syncedAt: current.syncedAt });
  };

  const run: OfflinePhotoStep['run'] = async ({ targets, lotsFailed, outcome }) => {
    for (const row of targets) {
      if (await host.halted()) return;
      // Re-read the switch, and skip an auction whose lot read failed: the network is likely down.
      if (!host.isSwitchedOn(row) || lotsFailed.has(row.id)) continue;
      if (outOfStorage.has(row.id)) {
        outcome.failed = true;
        outcome.storageFull = true;
        continue;
      }
      if (host.shouldStop(row.id)) return;
      try {
        const pass = await syncAuctionPhotos(row.id);
        if (pass == null) continue;
        if (pass.failed > 0) {
          const message = thumbnailFailureMessage(pass.failed, row.id);
          outcome.fail(message, new Error(message, { cause: pass.firstError ?? undefined }));
        }
        if (pass.stopped) return;
      } catch (err) {
        if (isOfflineStoreFullError(err)) outOfStorage.add(row.id);
        outcome.fail(`offline sync: the photos of auction ${row.id} failed`, err);
      }
    }
  };

  return {
    run,
    isRunning: (auctionId) => running === auctionId,
    clearOutOfStorage: (auctionIds) => {
      if (auctionIds === 'all') outOfStorage.clear();
      else for (const auctionId of auctionIds) outOfStorage.delete(auctionId);
    },
  };
}
